/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';
import {ReportService} from '../../../services/report.service';

@Component({
    selector: 'app-delete-prop-detail',
    templateUrl: './delete-prop-detail.component.html',
    styleUrl: './delete-prop-detail.component.scss',
    standalone: false
})
export class DeletePropDetailComponent {
  showSpinner: boolean
  file: File;
  sheets: string[];
  sheetNames: string[];

  constructor(readonly reportService: ReportService,
              readonly propulsionScenarioService: PropulsionScenarioService,
              readonly stateService: StateService,
              readonly excelService: ExcelExportService,
              public dialog: MatDialog,
              public snackBar: FabMessageService) {
  }

  /**
   * Exports the Propulsion Records for the propulsion scenario attached to the selected working scenario
   */
  getPropulsionRecords(): void {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const fileName = 'PropRecords_' + this.stateService.selectedScenario.propulsionScenario.id + "_" + currDate;

    this.showSpinner = true;
    this.reportService.getPropulsionRecordReport(this.stateService.getUserGmid(), this.stateService.selectedScenario.vehScenId).subscribe({
      next: (data) => {
        this.excelService.exportToExcelFromServer(data, fileName);
      },
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Successfully retrieved data. Exporting...', DISMISS_MSG);
      }
    });
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheetNames = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.sheetNames.length === 0) {
          this.file = null;
          this.snackBar.displayError("Please re-upload file", DISMISS_MSG)
        } else {
          this.openDeleteConfirmation();
        }
      }
    );
  }

  /**
   * Opens the delete confirmation to continue with deleting propulsion records
   */
  openDeleteConfirmation(): void {
    // Open confirmation dialog on what you are about to delete
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmation',
        message: 'This will delete propulsion records that were marked in the uploaded file. Continue?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    }).afterClosed().subscribe({
      next: confirmation => {
        if (confirmation) {
          this.validatePropDetailRecordsForDelete()
        }
      }
    });
  }

  /**
   * Validate the uploaded file that is used to delete propulsion records. If validation passes, proceed with deletion.
   */
  validatePropDetailRecordsForDelete(): void {
    this.showSpinner = true;
    let propScenId = null;
    this.propulsionScenarioService.validatePropDetailRecordsForDelete(this.file, this.sheetNames).subscribe({
      next: (data) => {
        propScenId = data;
      },
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.deletePropRecords(propScenId);
      }
    });
  }

  /**
   * Delete propulsion records that are marked in the uploaded file
   * @param propScenId The propulsion scenario ID found in the file
   */
  deletePropRecords(propScenId: number): void {
    this.showSpinner = true;
    this.propulsionScenarioService.deletePropulsionRecords(this.file, this.sheetNames, this.stateService.getUserGmid(), propScenId).subscribe({
      next: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Any marked propulsion records have been successfully deleted', DISMISS_MSG);
      },
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    });
  }
}
